import { formatNumericInput } from '@register-ui/utils';
import { mergeClassNames } from '@moniebook-web/utils';
import {
    type ChangeEvent,
    type DetailedHTMLProps,
    type ForwardedRef,
    forwardRef,
    type InputHTMLAttributes,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';

type InputProps = DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
>;

type NumericInputProps = Omit<InputProps, 'onChange' | 'style' | 'value'> & {
    onChange: (value: string) => void;
    minimumWidth?: number;
    value?: string;
    decimalPlaces?: number;
    min?: number;
    max?: number;
};

const countCommas = (str: string) => (str.match(/,/g) || []).length;

function NumericInputWithRef(
    props: Readonly<NumericInputProps>,
    ref: ForwardedRef<HTMLInputElement>,
) {
    const {
        onChange,
        minimumWidth = 1,
        placeholder,
        value = '',
        decimalPlaces = 0,
        min = 0,
        max = Infinity,
        className,
        ...restProps
    } = props;

    const internalRef = useRef<HTMLInputElement | null>(null);
    const cursorPosRef = useRef<number | null>(null);

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const inputValue = input.value;
        const caretPos = input.selectionStart ?? 0;

        const output = formatNumericInput(inputValue, {
            maxDecimalPlaces: decimalPlaces,
            min,
            max,
            disallowOutOfRangeValues: true,
        });

        if (output !== undefined) {
            const commasBefore = countCommas(inputValue);
            const commasAfter = countCommas(output);
            const newCursorPos = caretPos + (commasAfter - commasBefore);

            if (output === value) {
                input.value = value;
                input?.setSelectionRange(newCursorPos, newCursorPos);
            } else {
                cursorPosRef.current = newCursorPos;
                onChange(output);
            }
        }
    };

    useLayoutEffect(() => {
        if (cursorPosRef.current !== null && internalRef.current) {
            internalRef.current.setSelectionRange(
                cursorPosRef.current,
                cursorPosRef.current,
            );
            cursorPosRef.current = null;
        }
    });

    const setRefs = (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    };

    const inputStyles = useMemo(
        () => ({
            width: `${Math.max(minimumWidth, value.length)}ch`,
        }),
        [value, minimumWidth],
    );

    return (
        <input
            {...restProps}
            ref={setRefs}
            autoFocus
            className={mergeClassNames(
                'max-w-10ch px-0 text-center text-48 font-medium text-content-body-default placeholder:text-48 placeholder:text-content-label-default focus-visible:outline-none',
                className,
            )}
            style={inputStyles}
            onChange={handleOnChange}
            value={value}
            placeholder={placeholder}
            inputMode='decimal'
        />
    );
}

export const NumericInput = forwardRef(NumericInputWithRef);
